import { api } from "./api/client";

/** El navegador suporta Web Push i tenim service worker? */
export function isPushSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export type PushState = "unsupported" | "denied" | "subscribed" | "unsubscribed";

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Estat actual de la subscripció d'aquest dispositiu. */
export async function getPushState(): Promise<PushState> {
  if (!isPushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return sub ? "subscribed" : "unsubscribed";
}

/**
 * Demana permís i subscriu aquest dispositiu al Web Push, registrant-lo al
 * backend. Retorna el nou estat. Llança amb missatge si el permís es denega.
 */
export async function enablePush(): Promise<PushState> {
  if (!isPushSupported()) return "unsupported";

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "denied";

  const { publicKey, enabled } = await api.get<{ publicKey: string | null; enabled: boolean }>(
    "/push/public-key"
  );
  if (!enabled || !publicKey) {
    throw new Error("El servidor no té les notificacions push configurades.");
  }

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
  });
  return "subscribed";
}

export interface PushDiagnostics {
  supported: boolean;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  /** Valor cru de Notification.permission: "granted" | "denied" | "default". */
  rawPermission: string;
  swControllerState: string;
  hasExistingSubscription: boolean;
  requestPermissionError: string | null;
}

/** Recull dades crues del navegador per diagnosticar per què el push no s'activa. */
export async function getPushDiagnostics(): Promise<PushDiagnostics> {
  const hasServiceWorker = "serviceWorker" in navigator;
  const hasPushManager = "PushManager" in window;
  const hasNotification = "Notification" in window;
  const rawPermission = hasNotification ? Notification.permission : "n/a";

  let swControllerState = "n/a";
  let hasExistingSubscription = false;
  if (hasServiceWorker) {
    try {
      const reg = await navigator.serviceWorker.ready;
      swControllerState = reg.active?.state ?? "sense worker actiu";
      if (hasPushManager) {
        const sub = await reg.pushManager.getSubscription();
        hasExistingSubscription = !!sub;
      }
    } catch (e: any) {
      swControllerState = "error: " + (e?.message ?? String(e));
    }
  }

  return {
    supported: hasServiceWorker && hasPushManager && hasNotification,
    hasServiceWorker,
    hasPushManager,
    hasNotification,
    rawPermission,
    swControllerState,
    hasExistingSubscription,
    requestPermissionError: null,
  };
}

/** Cancel·la la subscripció d'aquest dispositiu (local + backend). */
export async function disablePush(): Promise<PushState> {
  if (!isPushSupported()) return "unsupported";
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  return "unsubscribed";
}
